import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

const CONTROL =
  'w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-ink placeholder:text-slate-400 ' +
  'transition duration-200 ease-out outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 ' +
  'disabled:bg-slate-50 disabled:text-slate-400';

const INVALID = 'border-red-400 focus:border-red-500 focus:ring-red-500/15';

interface FieldFrameProps {
  label: string;
  hint?: string;
  /** Validation message. Replaces the hint and turns the control red. */
  error?: string;
  optional?: boolean;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

/** Shared label / hint scaffolding so inputs and selects stay in step. */
function FieldFrame({ label, hint, error, optional, children }: FieldFrameProps) {
  const id = useId();
  const note = error ?? hint;
  const noteId = note ? `${id}-note` : undefined;

  return (
    <div className="block">
      <label
        htmlFor={id}
        className="mb-1.5 flex items-baseline justify-between gap-3 text-sm font-semibold text-ink"
      >
        <span>{label}</span>
        {optional && <span className="text-xs font-medium text-slate-400">Optional</span>}
      </label>
      {children({ id, describedBy: noteId })}
      {note && (
        <span
          id={noteId}
          className={`mt-1.5 block text-xs ${error ? 'font-medium text-red-600' : 'text-slate-500'}`}
        >
          {note}
        </span>
      )}
    </div>
  );
}

/** Props every control in this file shares on top of its native attributes. */
interface ControlProps {
  label: string;
  hint?: string;
  error?: string;
  invalid?: boolean;
  /** Renders an "Optional" marker beside the label. */
  optional?: boolean;
}

export function Input({
  label,
  hint,
  error,
  invalid,
  optional,
  className = '',
  ...props
}: InputHTMLAttributes<HTMLInputElement> & ControlProps) {
  const bad = invalid || Boolean(error);
  return (
    <FieldFrame label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={bad || undefined}
          className={`${CONTROL} ${bad ? INVALID : ''} ${className}`}
          {...props}
        />
      )}
    </FieldFrame>
  );
}

export function SelectInput({
  label,
  hint,
  error,
  invalid,
  optional,
  children,
  className = '',
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & ControlProps) {
  const bad = invalid || Boolean(error);
  return (
    <FieldFrame label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy }) => (
        <select
          id={id}
          aria-describedby={describedBy}
          aria-invalid={bad || undefined}
          className={`${CONTROL} appearance-none bg-[length:1.1rem] bg-[right:0.9rem_center] bg-no-repeat pr-10 ${
            bad ? INVALID : ''
          } ${className}`}
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
          }}
          {...props}
        >
          {children}
        </select>
      )}
    </FieldFrame>
  );
}

export function Textarea({
  label,
  hint,
  error,
  invalid,
  optional,
  className = '',
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & ControlProps) {
  const bad = invalid || Boolean(error);
  return (
    <FieldFrame label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy }) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={bad || undefined}
          className={`${CONTROL} min-h-24 resize-y leading-relaxed ${bad ? INVALID : ''} ${className}`}
          {...props}
        />
      )}
    </FieldFrame>
  );
}
